/** Thin wrapper around the browser EyeDropper API.
 *
 *  Spec: https://developer.mozilla.org/en-US/docs/Web/API/EyeDropper
 *
 *  Support (as of 2026-05): Chrome 95+, Edge 95+. Firefox + Safari
 *  do not implement it. Callers feature-detect via
 *  `isEyeDropperSupported()` and hide the affordance entirely on
 *  unsupported browsers — falling back to "use the existing
 *  canvas-side eyedropper tool" would mean closing the popover and
 *  re-opening it, which is worse UX than not offering the button. */

interface EyeDropperResult {
  sRGBHex: string;
}

interface EyeDropperConstructor {
  new (): { open(options?: { signal?: AbortSignal }): Promise<EyeDropperResult> };
}

declare global {
  interface Window {
    EyeDropper?: EyeDropperConstructor;
  }
}

/** Synchronous capability probe. Cheap; safe to call every render. */
export function isEyeDropperSupported(): boolean {
  return typeof window !== 'undefined' && typeof window.EyeDropper === 'function';
}

/** Open the browser eyedropper. Resolves to a `#rrggbb` hex or
 *  `null` if the user dismissed it (Escape, blur). The caller is
 *  expected to feature-detect first; if the API is missing this
 *  resolves to `null` so callers don't need a separate guard. */
export async function pickColorWithEyeDropper(signal?: AbortSignal): Promise<string | null> {
  if (!isEyeDropperSupported() || typeof window === 'undefined' || !window.EyeDropper) {
    return null;
  }
  try {
    const dropper = new window.EyeDropper();
    const result = await dropper.open({ signal });
    return result.sRGBHex.toLowerCase();
  } catch {
    // AbortError on dismiss, or any other failure — silent so the
    // picker doesn't show a transient error toast on a normal
    // user cancel.
    return null;
  }
}
